import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import { EditTextareaField } from '../../../EditTextareaField';
import type { ContactAddress3 } from '../queries/contactAddressesQuery';
import { GET_CONTACT_ADDRESSES } from '../queries/contactAddressesQuery';

const UPSERT_CONTACT_PRIMARY_ADDRESS = gql`
  mutation UpsertContactPrimaryAddress(
    $contactId: ID!
    $values: ContactPrimaryAddressUpsertInput!
  ) {
    upsertContactPrimaryAddress(contactId: $contactId, values: $values) {
      ok
      created
      contactPrimaryAddress {
        id
        address3
        address3City
        address3Street1
        address3Street2
        address3StateProvince
        address3ZipPostalCode
        address3CountryRegion
      }
    }
  }
`;

export interface EditAddress3DialogProps {
  contactId: string;
  initialValue: ContactAddress3;
  isOpen: boolean;
  onChange: (data: ContactAddress3) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactAddress3;
}

export function EditAddress3Dialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditAddress3DialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [upsertAddress] = useMutation(UPSERT_CONTACT_PRIMARY_ADDRESS, {
    refetchQueries: [
      { query: GET_CONTACT_ADDRESSES, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await upsertAddress({
        variables: { contactId, values: changes }
      });

      const data = response.data as {
        upsertContactPrimaryAddress?: { ok?: boolean };
      };

      if (data.upsertContactPrimaryAddress?.ok === false) {
        toast.error('Failed to save address 3');
      } else {
        toast.success('Address 3 saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving address 3:', error);
      toast.error('Failed to save address 3');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Address 3"
    >
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
            Location
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <EditFormField
              label="Street 1"
              onChange={street1 => {
                onChange({ ...value, street1 });
              }}
              value={value.street1}
            />
            <EditFormField
              label="Street 2"
              onChange={street2 => {
                onChange({ ...value, street2 });
              }}
              value={value.street2}
            />
            <EditFormField
              label="City"
              onChange={city => {
                onChange({ ...value, city });
              }}
              value={value.city}
            />
            <EditFormField
              label="State/Province"
              onChange={stateProvince => {
                onChange({ ...value, stateProvince });
              }}
              value={value.stateProvince}
            />
            <EditFormField
              label="ZIP/Postal Code"
              onChange={postalCode => {
                onChange({ ...value, postalCode });
              }}
              value={value.postalCode}
            />
            <EditFormField
              label="Country/Region"
              onChange={countryRegion => {
                onChange({ ...value, countryRegion });
              }}
              value={value.countryRegion}
            />
          </div>
        </div>

        <div className="col-span-2 mt-4">
          <EditTextareaField
            label="Full Address"
            onChange={fullAddress => {
              onChange({ ...value, fullAddress });
            }}
            value={value.fullAddress}
          />
        </div>
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactAddress3,
  initial: ContactAddress3
): Record<string, null | string> {
  const changes: Record<string, null | string> = {};

  if (current.street1 !== initial.street1)
    changes.address3Street1 = current.street1 || null;
  if (current.street2 !== initial.street2)
    changes.address3Street2 = current.street2 || null;
  if (current.city !== initial.city)
    changes.address3City = current.city || null;
  if (current.stateProvince !== initial.stateProvince)
    changes.address3StateProvince = current.stateProvince || null;
  if (current.postalCode !== initial.postalCode)
    changes.address3ZipPostalCode = current.postalCode || null;
  if (current.countryRegion !== initial.countryRegion)
    changes.address3CountryRegion = current.countryRegion || null;
  if (current.fullAddress !== initial.fullAddress)
    changes.address3 = current.fullAddress || null;

  return changes;
}
